"use client";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { SearchIcon, CloseIcon } from '../icons';
import { useSearchService } from '../../hooks/useSearchService';
import { Card, CardLocale, getCardTitle } from '../../types/card';
import { useLocale } from 'next-intl';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import LogoLoader from '../LogoLoader/LogoLoader';
import { getTitleSuffix } from '@/constants';
import { CardImage } from '../ui/CardImage/CardImage';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  placeholder?: string;
  onNavigate?: () => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ placeholder = 'Search for cards...', onNavigate }) => {
  const locale = useLocale() as CardLocale;
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(Card)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { performSearch, isLoading } = useSearchService();
  const router = useRouter();
  const { localizeFullPath } = useLocalizedPath();

  // Handle clicking outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for cards as user types with debounce
  useEffect(() => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      const results = await performSearch(query.trim());
      setSearchResults(results || []);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(value.trim().length >= 2);
  }, []);

  const handleClearSearch = useCallback(() => {
    setQuery('');
    setSearchResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  }, []);

  const handleResultClick = useCallback(() => {
    setShowResults(false);
    onNavigate?.();
  }, [onNavigate]);

  // router.push, not window.location.href: the latter is a hard navigation
  // that tears down the app shell on every search. And the target must go
  // through localizeFullPath like handleResultClick above does - proxy.ts
  // derives the locale from the first path segment, so a bare "/giftcards"
  // IS the default locale. Submitting the form used to drop a Romanian or
  // French visitor onto the English listing; only typed queries hit this
  // path (picking a suggestion was already localized), so it silently
  // punished the users searching hardest.
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    router.push(`${localizeFullPath('/giftcards')}?search=${encodeURIComponent(query.trim())}`);
    setShowResults(false);
    onNavigate?.();
  }, [query, router, localizeFullPath, onNavigate]);

  const handleFocus = useCallback(() => {
    if (query.trim().length >= 2) {
      setShowResults(true);
    }
  }, [query]);

  return (
    <div
      ref={searchRef}
      style={{ width: '100%', position: 'relative' }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setShowResults(false);
      }}
    >
      <form className={styles.searchForm} onSubmit={handleSearchSubmit} role="search">
        <input
          ref={inputRef}
          className={styles.searchInput}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          aria-label="Search input"
        />
        <button
          type="submit"
          aria-label="Search"
          className={`${styles.iconButton} ${query ? styles.searchButtonWithClear : styles.searchButton}`}
        >
          {isLoading ? (
            <LogoLoader size={16} color="var(--color-text-muted)" />
          ) : (
            <SearchIcon style={{ fontSize: 18 }} />
          )}
        </button>
        {query && (
          <button
            onClick={handleClearSearch}
            type="button"
            aria-label="Clear search"
            className={`${styles.iconButton} ${styles.clearButton}`}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        )}
      </form>

      <div className={`${styles.searchResults}${showResults ? ` ${styles.visible}` : ''}`}>
        {isLoading && searchResults.length === 0 ? (
          <div className={styles.noResults}>
            <LogoLoader size={20} color="var(--color-text)" />
            <span style={{ marginLeft: '8px' }}>Searching...</span>
          </div>
        ) : searchResults && searchResults.length === 0 && query.trim().length >= 2 ? (
          <div className={styles.noResults}>No results found</div>
        ) : (
          searchResults.map(card => (
            <Link
              key={card._id}
              href={localizeFullPath(`/giftcards/${card.slug}`)}
              className={styles.resultItem}
              onClick={handleResultClick}
            >
              <CardImage card={card} locale={locale} size="searchResultSmall" />
              <div className={styles.resultInfo}>
                <h4 className={styles.resultTitle}>{`${getCardTitle(card, locale)}${getTitleSuffix()}`}</h4>
                <p className={styles.resultBrand}>{card.slug}</p>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}; 